/**
 * Campaign Lookups Service
 *
 * CRUD wrappers for campaign_lookups + campaign_lookup_rows, plus the
 * four SECURITY DEFINER RPCs from the migration:
 *
 *   - replace_lookup_rows(lookup_id, rows, key_column, mode)
 *   - rename_lookup_column(lookup_id, old_name, new_name)
 *   - change_lookup_key_column(lookup_id, new_key_column)
 *   - set_lookup_default_row(lookup_id, row_id)
 *
 * Single-row inline edits use the supabase-js client directly. Multi-
 * row / multi-table mutations go through the RPCs so they're atomic.
 */

import { supabase } from "./supabase";
import type {
  CampaignLookup,
  CampaignLookupRow,
  CreateLookupInput,
  UpdateLookupInput,
  LookupRowUploadRow,
} from "../types/lookup.types";
import type { FieldMapping, LookupBucket } from "./templates.service";

const LOOKUP_PREVIEW_PAGE_SIZE = 1000;
const LOOKUP_PREVIEW_HARD_CAP = 50_000;

/**
 * Load exactly the lookup rows referenced by a mapping set for browser
 * preview. This mirrors the edge-function index shape and paginates past
 * PostgREST's 1,000-row response cap. Read failures are fail-loud so the
 * preview never presents an empty fallback as if it were real content.
 */
export async function loadLookupIndexForPreview(
  mappings: FieldMapping[],
): Promise<Map<string, LookupBucket>> {
  const lookupIds = Array.from(
    new Set(
      mappings
        .filter((m) => m.sourceType === "lookup" && m.lookupId)
        .map((m) => m.lookupId as string),
    ),
  );
  const index = new Map<string, LookupBucket>();
  if (lookupIds.length === 0) {
    return index;
  }

  let offset = 0;
  let loadedCount = 0;
  let hasMore = true;
  while (hasMore) {
    const { data, error } = await supabase
      .from("campaign_lookup_rows")
      .select("lookup_id, key_value_normalized, data, is_default")
      .in("lookup_id", lookupIds)
      .order("lookup_id", { ascending: true })
      .order("key_value_normalized", { ascending: true })
      .range(offset, offset + LOOKUP_PREVIEW_PAGE_SIZE - 1);
    if (error) {
      throw new Error(`Lookup preview load failed: ${error.message}`);
    }
    const page = (data ?? []) as Array<{
      lookup_id: string;
      key_value_normalized: string;
      data: Record<string, unknown>;
      is_default: boolean;
    }>;
    loadedCount += page.length;
    if (loadedCount > LOOKUP_PREVIEW_HARD_CAP) {
      throw new Error(
        `Lookup preview exceeds the ${LOOKUP_PREVIEW_HARD_CAP.toLocaleString()}-row safety limit.`,
      );
    }
    for (const row of page) {
      let bucket = index.get(row.lookup_id);
      if (!bucket) {
        bucket = { byKey: new Map(), defaultRow: null };
        index.set(row.lookup_id, bucket);
      }
      bucket.byKey.set(row.key_value_normalized, row.data);
      if (row.is_default) {
        bucket.defaultRow = row.data;
      }
    }
    if (page.length < LOOKUP_PREVIEW_PAGE_SIZE) {
      hasMore = false;
    } else {
      offset += LOOKUP_PREVIEW_PAGE_SIZE;
    }
  }
  return index;
}

// =====================================================
// LOOKUPS — CRUD
// =====================================================

export async function getLookups(): Promise<CampaignLookup[]> {
  const { data, error } = await supabase
    .from("campaign_lookups")
    .select("*")
    .order("name", { ascending: true });
  if (error) {
    throw error;
  }
  return (data || []) as CampaignLookup[];
}

export async function getLookup(id: string): Promise<CampaignLookup | null> {
  const { data, error } = await supabase
    .from("campaign_lookups")
    .select("*")
    .eq("id", id)
    .maybeSingle();
  if (error) {
    throw error;
  }
  return data as CampaignLookup | null;
}

export async function createLookup(
  input: CreateLookupInput,
): Promise<CampaignLookup> {
  const { data, error } = await supabase
    .from("campaign_lookups")
    .insert({
      name: input.name,
      description: input.description ?? null,
      key_column: input.key_column,
      column_schema: input.column_schema,
    })
    .select()
    .single();
  if (error) {
    throw error;
  }
  return data as CampaignLookup;
}

export async function updateLookup(
  id: string,
  input: UpdateLookupInput,
): Promise<CampaignLookup> {
  const { data, error } = await supabase
    .from("campaign_lookups")
    .update(input)
    .eq("id", id)
    .select()
    .single();
  if (error) {
    throw error;
  }
  return data as CampaignLookup;
}

export async function deleteLookup(id: string): Promise<void> {
  const { error } = await supabase
    .from("campaign_lookups")
    .delete()
    .eq("id", id);
  if (error) {
    throw error;
  }
}

// =====================================================
// LOOKUP ROWS — CRUD
// =====================================================

export async function getLookupRows(
  lookupId: string,
): Promise<CampaignLookupRow[]> {
  const { data, error } = await supabase
    .from("campaign_lookup_rows")
    .select("*")
    .eq("lookup_id", lookupId)
    .order("row_order", { ascending: true });
  if (error) {
    throw error;
  }
  return (data || []) as CampaignLookupRow[];
}

export async function getLookupRowCount(lookupId: string): Promise<number> {
  const { count, error } = await supabase
    .from("campaign_lookup_rows")
    .select("*", { count: "exact", head: true })
    .eq("lookup_id", lookupId);
  if (error) {
    throw error;
  }
  return count ?? 0;
}

/**
 * Insert a single new row. Used by the "Add Row" button in the editor.
 * The unique (lookup_id, key_value_normalized) index will reject
 * duplicates with a Postgres 23505 — caller surfaces the message inline.
 */
export async function insertLookupRow(
  lookupId: string,
  keyColumn: string,
  data: Record<string, unknown>,
): Promise<CampaignLookupRow> {
  const keyDisplay = String(data[keyColumn] ?? "").trim();
  const keyNormalized = keyDisplay.toLowerCase();
  // Place at the end of the row order
  const { count } = await supabase
    .from("campaign_lookup_rows")
    .select("*", { count: "exact", head: true })
    .eq("lookup_id", lookupId);
  const { data: row, error } = await supabase
    .from("campaign_lookup_rows")
    .insert({
      lookup_id: lookupId,
      key_value_normalized: keyNormalized,
      key_value_display: keyDisplay,
      data,
      is_default: false,
      row_order: count ?? 0,
    })
    .select()
    .single();
  if (error) {
    throw error;
  }
  return row as CampaignLookupRow;
}

/**
 * Update a single cell in an existing row. The data object is
 * merge-replaced on the server side via the standard supabase update;
 * the caller passes the FULL data object (post-edit), not a partial.
 *
 * If the change touches the key column, key_value_normalized and
 * key_value_display must also be passed so the indexed match still
 * works at send time.
 */
export async function updateLookupRow(
  rowId: string,
  patch: Partial<
    Pick<
      CampaignLookupRow,
      "data" | "key_value_normalized" | "key_value_display"
    >
  >,
): Promise<CampaignLookupRow> {
  const { data, error } = await supabase
    .from("campaign_lookup_rows")
    .update(patch)
    .eq("id", rowId)
    .select()
    .single();
  if (error) {
    throw error;
  }
  return data as CampaignLookupRow;
}

export async function deleteLookupRow(rowId: string): Promise<void> {
  const { error } = await supabase
    .from("campaign_lookup_rows")
    .delete()
    .eq("id", rowId);
  if (error) {
    throw error;
  }
}

// =====================================================
// RPCs — atomic multi-row mutations
// =====================================================

/**
 * Bulk insert/upsert rows. Used by CSV upload (replace or merge mode).
 * The RPC validates row count, column count, at-most-one-default,
 * and that p_key_column is one of p_column_names — all server-side
 * before any data mutation. Atomically updates
 * campaign_lookups.column_schema + key_column from columnNames so
 * the editor's column rendering and the variable mapper's column
 * dropdowns stay in sync with the rows we just wrote.
 */
export async function replaceLookupRows(
  lookupId: string,
  rows: LookupRowUploadRow[],
  keyColumn: string,
  mode: "merge" | "replace",
  columnNames: string[],
): Promise<number> {
  const { data, error } = await supabase.rpc("replace_lookup_rows", {
    p_lookup_id: lookupId,
    p_rows: rows,
    p_key_column: keyColumn,
    p_mode: mode,
    p_column_names: columnNames,
  });
  if (error) {
    throw error;
  }
  return (data as number) ?? 0;
}

export async function renameLookupColumn(
  lookupId: string,
  oldName: string,
  newName: string,
): Promise<void> {
  const { error } = await supabase.rpc("rename_lookup_column", {
    p_lookup_id: lookupId,
    p_old_name: oldName,
    p_new_name: newName,
  });
  if (error) {
    throw error;
  }
}

export async function changeLookupKeyColumn(
  lookupId: string,
  newKeyColumn: string,
): Promise<void> {
  const { error } = await supabase.rpc("change_lookup_key_column", {
    p_lookup_id: lookupId,
    p_new_key_column: newKeyColumn,
  });
  if (error) {
    throw error;
  }
}

export async function setLookupDefaultRow(
  lookupId: string,
  rowId: string,
): Promise<void> {
  const { error } = await supabase.rpc("set_lookup_default_row", {
    p_lookup_id: lookupId,
    p_row_id: rowId,
  });
  if (error) {
    throw error;
  }
}

// =====================================================
// USAGE — referenced-by lookup
// =====================================================

/**
 * Find campaign_flights whose email_template_data.field_mappings include
 * a sourceType='lookup' mapping referencing this lookup. Grouped by
 * status so the UI can hard-block delete vs. warn-but-allow per the
 * audit's #70 design (scheduled/sending hard-block; draft warn).
 */
export async function getLookupReferencingFlights(lookupId: string): Promise<
  Array<{
    id: string;
    name: string;
    status: string;
    campaign_id: string | null;
  }>
> {
  // Use the @> JSONB containment operator on the nested array path.
  // PostgREST supports cs (contains) on JSONB — but we want any element
  // matching, so we use the JSONB path query.
  //
  // Keyset-paged by id: PROD has >1000 flights with email_template_data, and
  // a single select stopped at PostgREST's 1000-row cap, so this delete guard
  // could miss a referencing flight and allow an unsafe lookup delete. Keyset
  // (id > last seen) rather than offset so a flight deleted mid-read can never
  // shift a later row out of the scan, and it stops only on an EMPTY page, so
  // it stays complete even if max_rows were ever lowered below the page size.
  const data: Array<Record<string, unknown>> = [];
  let lastId: string | null = null;
  for (;;) {
    let query = supabase
      .from("campaign_flights")
      // campaign(is_deleted): flights of a soft-deleted campaign (migration 203) can never send, so they neither
      // block nor warn about deleting a lookup.
      .select(
        "id, name, status, campaign_id, email_template_data, campaign:campaigns(is_deleted)",
      )
      .not("email_template_data", "is", null);
    if (lastId !== null) {
      query = query.gt("id", lastId);
    }
    const { data: page, error } = await query
      .order("id", { ascending: true })
      .limit(1000);
    if (error) {
      throw error;
    }
    const rows = (page ?? []) as Array<Record<string, unknown>>;
    if (rows.length === 0) {
      break;
    }
    data.push(...rows);
    lastId = rows[rows.length - 1].id as string;
  }

  // Filter client-side for rows whose field_mappings include a lookup
  // mapping with this lookup_id. Doable in PostgREST with deeper @>
  // syntax, but the row count for non-null email_template_data is
  // small in practice.
  const referencing: Array<{
    id: string;
    name: string;
    status: string;
    campaign_id: string | null;
  }> = [];
  for (const row of (data || []) as Array<{
    id: string;
    name: string;
    status: string;
    campaign_id: string | null;
    email_template_data: {
      field_mappings?: Array<{ sourceType?: string; lookupId?: string }>;
    } | null;
    campaign?: { is_deleted?: boolean | null } | null;
  }>) {
    if (row.campaign?.is_deleted === true) {
      continue;
    }
    const mappings = row.email_template_data?.field_mappings ?? [];
    if (
      mappings.some((m) => m.sourceType === "lookup" && m.lookupId === lookupId)
    ) {
      referencing.push({
        id: row.id,
        name: row.name,
        status: row.status,
        campaign_id: row.campaign_id,
      });
    }
  }
  return referencing;
}
